import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { CLO_CHANNELS, cloError, cloGet, cloPost, ENDPOINTS, formatDate, pounds } from "./api";
import { useRetailers } from "./useRetailers";

const UnknownMidsTab: React.FC = () => {
  const notify = useNotify();
  const retailers = useRetailers();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [assigning, setAssigning] = useState<any | null>(null);
  const [brandId, setBrandId] = useState("");
  const [channel, setChannel] = useState("in_store");

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_UNKNOWN_MIDS, { page: page + 1, pageSize });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (row: any, action: "assign" | "reject", targetBrand: string) => {
    try {
      await cloPost(ENDPOINTS.CLO_UNKNOWN_MIDS, {
        action,
        network: row.network,
        mid: row.mid,
        brandId: Number(targetBrand),
        merchantName: row.merchant_name || null,
        ...(action === "assign" ? { channel } : {}),
      });
      notify(action === "assign" ? "MID assigned; held purchases will be rewarded on the next retry" : "MID rejected", "info");
      setAssigning(null);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  return (
    <Paper>
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          Purchases at a MID we don't know are held, not dropped. Assign the MID to a retailer and channel to release them, or reject it.
          Held purchases expire after the retailer's pending period.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Network</TableCell>
            <TableCell>MID</TableCell>
            <TableCell>Merchant name</TableCell>
            <TableCell>Suggested retailer</TableCell>
            <TableCell>Purchases</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>First / last seen</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.network}:${r.mid}`}>
              <TableCell>{r.network}</TableCell>
              <TableCell>{r.mid}</TableCell>
              <TableCell>{r.merchant_name || "—"}</TableCell>
              <TableCell>{r.suggested_retailer || "—"}</TableCell>
              <TableCell>{r.purchases}</TableCell>
              <TableCell>{pounds(r.value_minor)}</TableCell>
              <TableCell>
                <Typography variant="caption" display="block">{formatDate(r.first_seen)}</Typography>
                <Typography variant="caption" display="block">{formatDate(r.last_seen)}</Typography>
              </TableCell>
              <TableCell>
                <Button size="small" color="primary" onClick={() => { setBrandId(r.suggested_brand_id ? String(r.suggested_brand_id) : ""); setAssigning(r); }}>Assign</Button>
                <Button size="small" color="secondary" disabled={!r.suggested_brand_id} onClick={() => submit(r, "reject", String(r.suggested_brand_id))}>Reject</Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center">No held purchases at unknown MIDs</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={assigning !== null} onClose={() => setAssigning(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign MID {assigning?.mid}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth margin="dense" label="Retailer" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {retailers.map((r) => <MenuItem key={r.brand_id} value={String(r.brand_id)}>{r.display_name}</MenuItem>)}
          </TextField>
          <TextField select fullWidth margin="dense" label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
            {CLO_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssigning(null)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={!brandId} onClick={() => submit(assigning, "assign", brandId)}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UnknownMidsTab;
